import PublicationHistory from '@/pages/Editor/components/Publication/PublicationHistory.tsx';
import { Group, Tabs } from '@mantine/core';
import { useEffect, useState } from 'react';
import PublishedVersionsTab from '@/pages/Editor/components/VersionHistory/VersionHistoryModal/PublishedVersionsTab.tsx';
import StyledModal from '@/components/StyledModal/StyledModal.tsx';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import AllVersionsTab from '@/pages/Editor/components/VersionHistory/VersionHistoryModal/AllVersionsTab.tsx';
import { Automation, AutomationVersionSimple } from '@/types/automations.ts';
import VersionComparisonModal from '@/pages/Editor/components/VersionHistory/VersionComparison/VersionComparisonModal.tsx';
import { VersionHistoryReviewContext } from '@/pages/Editor/components/VersionHistory/VersionHistoryModal/VersionHistoryReviewContext.ts';

interface VersionSelectionModalProps {
  opened: boolean;
  onClose: () => void;
  automation: Automation;
  latestVersion?: AutomationVersionSimple;
}

type tabs = 'published' | 'autosaved' | 'activation';

const VersionHistoryModal = ({
  opened,
  onClose,
  automation,
  latestVersion,
}: VersionSelectionModalProps) => {
  const [activeTab, setActiveTab] = useState<tabs>('published');
  const [comparisonOpened, setComparisonOpened] = useState(false);
  const [comparisonTarget, setComparisonTarget] = useState<AutomationVersionSimple | undefined>();

  useEffect(() => {
    if (!opened) {
      setComparisonOpened(false);
      setComparisonTarget(undefined);
    }
  }, [opened]);

  return (
    <VersionHistoryReviewContext.Provider
      value={(targetVersion) => {
        setComparisonTarget(targetVersion);
        setComparisonOpened(true);
      }}
    >
      {!comparisonOpened && (
        <StyledModal
          opened={opened}
          onClose={onClose}
          title="Version History"
          centered
          withCloseButton={true}
        >
          <Group justify="flex-end" mb="sm">
            <StyledButton size="sm" variant="subtle" onClick={() => setComparisonOpened(true)}>
              Compare versions
            </StyledButton>
          </Group>
          <Tabs
            variant={'pills'}
            value={activeTab}
            onChange={(v) => setActiveTab(v as tabs)}
            color={'#ECECED'}
            styles={{
              tab: { padding: '4px 10px', fontSize: '13px' },
              tabLabel: { color: '#0F172A' },
              root: { marginLeft: '-16px', marginRight: '-16px' },
            }}
          >
            <Tabs.List>
              <Tabs.Tab value="activation">Release activity</Tabs.Tab>
              <Tabs.Tab
                value="published"
                style={{ fontWeight: activeTab === 'published' ? 600 : 'inherit' }}
              >
                Versions
              </Tabs.Tab>
              <Tabs.Tab
                value="autosaved"
                style={{ fontWeight: activeTab === 'autosaved' ? 600 : 'inherit' }}
              >
                Draft history
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="activation">
              <PublicationHistory automationId={automation.id} canEdit={!automation.retired_at} />
            </Tabs.Panel>
            <PublishedVersionsTab automation={automation} latestVersion={latestVersion} />
            <AllVersionsTab automation={automation} latestVersion={latestVersion} />
          </Tabs>
        </StyledModal>
      )}
      {comparisonOpened && (
        <VersionComparisonModal
          opened={comparisonOpened}
          onClose={() => {
            setComparisonOpened(false);
            setComparisonTarget(undefined);
          }}
          publishedVersion={automation.published_version}
          latestVersion={comparisonTarget || latestVersion}
          initialBaseVersionId={comparisonTarget ? automation.published_version?.id : undefined}
          initialTargetVersionId={comparisonTarget?.id}
        />
      )}
    </VersionHistoryReviewContext.Provider>
  );
};

export default VersionHistoryModal;
